import { useId, useState, useRef, DragEvent } from "react";

import { DropzoneStateEnums, FileType, UploadFileType } from "./types";

import IconUploadWhite from "./icon-upload-white.svg?react";
import IconX from "./icon-x.svg?react";
import { Button, Card, ProgressBar } from "tvs-unified-lib";

type FileUploadDropzoneType = {
  files: FileType[] | undefined;
  uploadingFiles: UploadFileType[] | undefined;
  state: DropzoneStateEnums;
  onFilesUpload: (files: FileList | null) => void;
  onRemoveFile: (index: number, type: string) => void;
  className?: string;
  accept?: string;
  acceptText?: string;
  withTimer?: boolean;
  multiple?: boolean;
};

const FileUploadDropzone = (props: FileUploadDropzoneType) => {
  const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false);

  const {
    files,
    uploadingFiles,
    state: componentState,
    onFilesUpload,
    onRemoveFile,
    className = "",
    accept,
    acceptText = "",
    withTimer = false,
    multiple = false,
  } = props;

  const componentId = useId();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const RenderTitle = () => {
    return (
      <span className="theme-file-dropzone-title">
        {componentState === "idle" && "Drag and drop the file here"}
        {componentState === "uploading" && "Uploading..."}
        {componentState === "upload-success" && "All File(s) Uploaded!"}
      </span>
    );
  };

  const RenderSubtitle = () => {
    return (
      <span className="theme-file-dropzone-subtitle">
        {componentState === "idle" && acceptText !== "" && acceptText}
        {componentState === "uploading" && "This won’t take too long!"}
        {componentState === "upload-success" &&
          "Your file(s) has been successfully uploaded"}
      </span>
    );
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(true);
    e.dataTransfer.dropEffect = "copy";
  };

  const handleFileDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(false);
    const selectedFiles: FileList = e.dataTransfer.files;
    if (selectedFiles) {
      for (const selectedFile of selectedFiles) {
        if (accept) {
          const acceptedFileTypes: string[] = accept.split(",");
          if (
            acceptedFileTypes.includes("image/*") &&
            !selectedFile.type.includes("image/*")
          ) {
            return false;
          } else if (!acceptedFileTypes.includes(selectedFile.type)) {
            return false;
          }
        }
      }
    }
    onFilesUpload(selectedFiles);
  };
  
  return (
    <Card
      className={`theme-file-dropzone ${
        isDraggingOver ? "theme-file-dropzone-dragged" : ""
      } ${className}`}
      onDragOver={handleDragOver}
      onDragLeave={() => setIsDraggingOver(false)}
      onDrop={(e) => {
        handleFileDrop(e);
        return false;
      }}
    >
      <input
        type="file"
        ref={fileInputRef}
        accept={accept ?? "*"}
        multiple={multiple}
        onChange={(e) => {
          onFilesUpload(e.target.files);
        }}
        className="theme-file-input"
      />
      {RenderTitle()}
      {RenderSubtitle()}
      {componentState === "idle" && (
        <Button
          className="theme-file-dropzone-button"
          label="Browse"
          icon={<IconUploadWhite />}
          iconPosition="left"
          onClick={() => {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            fileInputRef !== null &&
              fileInputRef.current !== null &&
              fileInputRef.current.click();
          }}
        />
      )}

      {componentState !== "idle" && (
        <div className="theme-file-dropzone-files">
          {componentState === "uploading" &&
            uploadingFiles &&
            uploadingFiles.map((file: UploadFileType, index: number) => {
              return (
                <div
                  className="theme-file-dropzone-file"
                  key={`dz-${componentId}-uploading-${index}`}
                >
                  <IconX
                    className="file-remove"
                    onClick={() => {
                      onRemoveFile(index, "uploadingFiles");
                      fileInputRef.current.value = "";
                    }}
                  />
                  <p className="file-name">{file.file.name}</p>
                  <ProgressBar
                    value={file.uploadPercent}
                    max={100}
                    uploadLabel="Uploading"
                    withTimer={withTimer}
                  />
                </div>
              );
            })}

          {componentState === "upload-success" &&
            files &&
            files.map((file: FileType, index: number) => {
              return (
                <div
                  className="theme-file-dropzone-file"
                  key={`dz-${componentId}-uploading-${index}`}
                >
                  <IconX
                    className="file-remove"
                    onClick={() => {
                      onRemoveFile(index, "uploadedFiles");
                      fileInputRef.current.value = "";
                    }}
                  />
                  <p className="file-name">
                    {typeof file.uri === "string" && file.uri}
                  </p>
                  <ProgressBar value={100} max={100} />
                  <p className="file-upload-message">Successfully uploaded</p>
                </div>
              );
            })}
        </div>
      )}
    </Card>
  );
};

export default FileUploadDropzone;
